import { Button } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Checkbox } from '../components/ui/checkbox'
// resources/js/pages/api_keys.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { type BreadcrumbProps, InputGroup, FormGroup } from '@blueprintjs/core'
import { Callout } from '../components/ui/callout'
import { axiosGet, axiosPost, axiosDestroy } from '../util/api'
import { Layout } from '../components/Layout'
import copy from 'copy-to-clipboard'
import { Toasts } from '../components/toaster/Toast'
import { KeyRound } from 'lucide-react'
import { EllipsisMenu } from '../icons/icons'
import { Menu, MenuItem } from '../components/ui/menu'
import publicApiScopeCatalog from '../generated/public-api-scope-catalog.json'
import { API_DOCUMENTATION_URL } from './help/api_documentation_redirect'

interface ApiKey {
    id: string
    name: string
    key_prefix: string
    scopes: string[]
    created_at: string
    last_used_at: string | null
    revoked_at: string | null
}

function getKeyStatus(key: ApiKey): 'active' | 'revoked' {
    return key.revoked_at ? 'revoked' : 'active'
}

interface CreateApiKeyResponse {
    api_key: ApiKey
    key: string
}

export const API_KEY_SCOPE_CATALOG = publicApiScopeCatalog
export const apiKeyRevokePath = (id: string) => `api-keys/${id}`

const PlusIcon = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" strokeWidth="1.8">
        <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeLinecap="round" />
    </svg>
)

const ExternalLinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path
            d="M5 3.5h5.5V9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M10.5 3.5L4 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const CreateApiKeyButton = ({
    onClick,
    disabled = false,
}: {
    onClick: () => void
    disabled?: boolean
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? 'Maximum of 10 active API keys reached' : undefined}
        className="inline-flex items-center gap-1.5 px-4 h-8 text-[13px] font-[500] text-white bg-[#1A6FD4] hover:bg-[#1562BE] disabled:bg-[#B8C7D9] disabled:cursor-not-allowed rounded-lg transition-colors"
    >
        <PlusIcon />
        Create API key
    </button>
)

export const ApiKeysPageHeader = () => (
    <div className="pb-8 pt-3">
        <h1 className="m-0 text-[24px] font-[700] leading-none tracking-[-0.03em] text-[#242529]">
            API Keys
        </h1>
        <p className="mt-1 mb-0 text-[14px] font-[400] leading-5 text-[#777A80]">
            Learn more about the{' '}
            <a
                href={API_DOCUMENTATION_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[#777A80] underline decoration-[#A7AAAF] underline-offset-2 transition-colors hover:text-[#242529] hover:decoration-[#242529]"
            >
                Merch Jar API
                <ExternalLinkIcon />
            </a>
        </p>
    </div>
)

const EmptyApiKeysState = ({ onCreate }: { onCreate: () => void }) => (
    <div className="flex min-h-[620px] flex-col items-center justify-center px-8 text-center">
        <div className="relative mb-16 h-[270px] w-[270px]">
            <div
                className="absolute inset-0 opacity-60"
                style={{
                    backgroundImage: 'radial-gradient(#D8DCE1 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            />
            <div className="absolute left-0 top-0 h-4 w-px bg-[#D8DCE1]" />
            <div className="absolute left-0 top-0 h-px w-4 bg-[#D8DCE1]" />
            <div className="absolute right-0 top-0 h-4 w-px bg-[#D8DCE1]" />
            <div className="absolute right-0 top-0 h-px w-4 bg-[#D8DCE1]" />
            <div className="absolute bottom-0 left-0 h-4 w-px bg-[#D8DCE1]" />
            <div className="absolute bottom-0 left-0 h-px w-4 bg-[#D8DCE1]" />
            <div className="absolute bottom-0 right-0 h-4 w-px bg-[#D8DCE1]" />
            <div className="absolute bottom-0 right-0 h-px w-4 bg-[#D8DCE1]" />
            <div className="absolute inset-0 flex items-center justify-center">
                <KeyRound
                    size={128}
                    strokeWidth={1.45}
                    className="text-[#D9DCE1] drop-shadow-[0_1px_0_rgba(255,255,255,0.9)]"
                />
            </div>
        </div>
        <h2 className="m-0 text-[24px] font-[650] leading-8 tracking-[-0.02em] text-[#242529]">
            No API keys
        </h2>
        <p className="mb-8 mt-2 text-[20px] font-[400] leading-7 text-[#777A80]">
            You don't have any API keys
        </p>
        <CreateApiKeyButton onClick={onCreate} />
    </div>
)

export const ApiKeys = () => {
    const [keys, setKeys] = useState<ApiKey[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Create dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [createName, setCreateName] = useState('')
    const [createScopes, setCreateScopes] = useState<string[]>(
        API_KEY_SCOPE_CATALOG.map((s) => s.id),
    )
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState<string | null>(null)

    // Reveal key dialog state
    const [revealedKey, setRevealedKey] = useState<string | null>(null)

    // Revoke dialog state
    const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
    const [revokingId, setRevokingId] = useState<string | null>(null)

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Settings' }]

    const loadKeys = useCallback(() => {
        setLoading(true)
        setError(null)
        axiosGet('api-keys')
            .then((res) => {
                setKeys(res.data)
            })
            .catch(() => {
                setError('Failed to load API keys')
            })
            .finally(() => {
                setLoading(false)
            })
    }, [])

    useEffect(() => {
        loadKeys()
    }, [loadKeys])

    const activeKeys = useMemo(() => keys.filter((k) => getKeyStatus(k) === 'active'), [keys])

    const revokedKeys = useMemo(() => keys.filter((k) => getKeyStatus(k) === 'revoked'), [keys])

    const sortedKeys = useMemo(() => [...activeKeys, ...revokedKeys], [activeKeys, revokedKeys])

    // --- Create API Key ---

    const handleOpenCreateDialog = useCallback(() => {
        setCreateDialogOpen(true)
        setCreateName('')
        setCreateScopes(API_KEY_SCOPE_CATALOG.map((s) => s.id))
        setCreateError(null)
    }, [])

    const handleCloseCreateDialog = useCallback((open: boolean) => {
        if (!open) {
            setCreateDialogOpen(false)
            setCreateName('')
            setCreateScopes(API_KEY_SCOPE_CATALOG.map((s) => s.id))
            setCreateError(null)
            setCreating(false)
        }
    }, [])

    const handleToggleScope = useCallback((scope: string) => {
        setCreateScopes((prev) =>
            prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
        )
    }, [])

    const handleCreateKey = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()

            if (!createName.trim()) {
                setCreateError('Name is required')
                return
            }

            if (createScopes.length === 0) {
                setCreateError('At least one scope is required')
                return
            }

            try {
                setCreateError(null)
                setCreating(true)
                const res = await axiosPost('api-keys', {
                    name: createName.trim(),
                    scopes: createScopes,
                })
                const data: CreateApiKeyResponse = res.data
                setCreateDialogOpen(false)
                setCreateName('')
                setCreateScopes(API_KEY_SCOPE_CATALOG.map((s) => s.id))
                setCreating(false)
                setRevealedKey(data.key)
                loadKeys()
                Toasts.success('API key created')
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to create API key'
                setCreateError(message)
                setCreating(false)
            }
        },
        [createName, createScopes, loadKeys],
    )

    // --- Reveal Key Dialog ---

    const handleCloseRevealDialog = useCallback(() => {
        setRevealedKey(null)
    }, [])

    const handleCopyKey = useCallback(() => {
        if (revealedKey) {
            copy(revealedKey)
            Toasts.success('API key copied to clipboard')
        }
    }, [revealedKey])

    // --- Revoke Key ---

    const handleOpenRevokeDialog = useCallback((key: ApiKey) => {
        setRevokeTarget(key)
    }, [])

    const handleCloseRevokeDialog = useCallback(() => {
        setRevokeTarget(null)
    }, [])

    const handleRevokeKey = useCallback(async () => {
        if (!revokeTarget) return

        try {
            setRevokingId(revokeTarget.id)
            await axiosDestroy(apiKeyRevokePath(revokeTarget.id))
            setRevokeTarget(null)
            loadKeys()
            Toasts.success('API key revoked')
        } catch {
            Toasts.error('Failed to revoke API key')
        } finally {
            setRevokingId(null)
        }
    }, [revokeTarget, loadKeys])

    // --- Formatting ---

    const formatDate = useCallback((dateStr: string | null): string => {
        if (!dateStr) return 'Never'
        const [year, month, day] = dateStr.split('T')[0].split('-').map(Number)
        const date = new Date(year, month - 1, day)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }, [])

    const keysContent = (
        <>
            <ApiKeysPageHeader />

            {error && (
                <Callout intent="danger" className="mt-6 mb-4">
                    {error}
                </Callout>
            )}

            {loading && (
                <div className="flex justify-center py-16">
                    <Button loading={true} minimal={true} />
                </div>
            )}

            {!loading && sortedKeys.length === 0 && (
                <EmptyApiKeysState onCreate={handleOpenCreateDialog} />
            )}

            {!loading && sortedKeys.length > 0 && (
                <div className="mt-6 overflow-hidden rounded-lg border border-[#DBE2E8] bg-white">
                    <div className="grid grid-cols-[1.3fr_1.35fr_2.8fr_1.1fr_40px] items-center gap-3 border-b border-[#EDF0F3] bg-[#FAFBFC] px-4 py-2 text-[11px] font-[600] uppercase tracking-[0.04em] text-[#94A3B8]">
                        <div>Name</div>
                        <div>Key</div>
                        <div>Scopes</div>
                        <div>Activity</div>
                        <div />
                    </div>
                    <div>
                        {sortedKeys.map((key) => {
                            const status = getKeyStatus(key)

                            return (
                                <div
                                    key={key.id}
                                    className={`group grid grid-cols-[1.3fr_1.35fr_2.8fr_1.1fr_40px] items-center gap-3 border-t border-[#EDF0F3] px-4 py-3 transition-colors first:border-t-0 hover:bg-[#FAFBFC] ${status === 'revoked' ? 'opacity-55' : ''}`}
                                >
                                    <div className="flex min-w-0 items-center gap-2">
                                        <span className="min-w-0 truncate text-[14px] font-[600] text-[#0E2947]">
                                            {key.name}
                                        </span>
                                        <span
                                            className={`inline-flex shrink-0 items-center gap-1.5 text-[12px] font-[500] ${status === 'active' ? 'text-emerald-600/90' : 'text-[#94A3B8]'}`}
                                        >
                                            <span
                                                className={`size-1.5 rounded-full ${status === 'active' ? 'bg-emerald-400' : 'bg-[#CBD5E1]'}`}
                                            />
                                            {status}
                                        </span>
                                    </div>
                                    <div>
                                        <code className="rounded bg-[#F1F3F6] px-2 py-1 text-[12px] text-[#7289A3]">
                                            {key.key_prefix}...
                                        </code>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {key.scopes.map((scope) => (
                                            <span
                                                key={scope}
                                                className="inline-flex items-center rounded-md bg-[#F1F3F6] px-2 py-0.5 text-[11px] font-[500] text-[#7289A3]"
                                            >
                                                {scope}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="space-y-0.5 text-[12px] leading-snug text-[#7289A3]">
                                        <div>
                                            <span className="text-[#94A3B8]">Created</span>{' '}
                                            {formatDate(key.created_at)}
                                        </div>
                                        <div>
                                            <span className="text-[#94A3B8]">Used</span>{' '}
                                            {formatDate(key.last_used_at)}
                                        </div>
                                    </div>
                                    <div className="flex justify-end opacity-0 transition-opacity group-hover:opacity-100">
                                        {status === 'active' && (
                                            <EllipsisMenu
                                                content={
                                                    <Menu>
                                                        <MenuItem
                                                            text={
                                                                revokingId === key.id
                                                                    ? 'Revoking...'
                                                                    : 'Revoke'
                                                            }
                                                            disabled={revokingId !== null}
                                                            onClick={() =>
                                                                handleOpenRevokeDialog(key)
                                                            }
                                                        />
                                                    </Menu>
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </>
    )

    const dialogs = (
        <>
            {/* Create API Key Dialog */}
            <Dialog
                isOpen={createDialogOpen}
                onClose={() => handleCloseCreateDialog(false)}
                title="Create API Key"
                className="bp4-card-rounded"
            >
                <form onSubmit={handleCreateKey}>
                    <div className="p-4">
                        {createError && (
                            <Callout intent="danger" className="mb-4">
                                {createError}
                            </Callout>
                        )}
                        <FormGroup
                            label="Name"
                            labelFor="api-key-name"
                            labelInfo="(required)"
                            className="mb-4"
                        >
                            <InputGroup
                                id="api-key-name"
                                placeholder="e.g., Production Integration"
                                value={createName}
                                onChange={(e) => setCreateName(e.target.value)}
                                disabled={creating}
                                autoFocus
                            />
                        </FormGroup>
                        <FormGroup label="Scopes" className="mb-0">
                            {API_KEY_SCOPE_CATALOG.map((scope) => (
                                <Checkbox
                                    key={scope.id}
                                    label={scope.label}
                                    checked={createScopes.includes(scope.id)}
                                    onChange={() => handleToggleScope(scope.id)}
                                    disabled={creating}
                                />
                            ))}
                        </FormGroup>
                    </div>
                    <div className="flex justify-end gap-2 p-4 border-t">
                        <Button
                            text="Cancel"
                            type="button"
                            onClick={() => handleCloseCreateDialog(false)}
                            disabled={creating}
                        />
                        <Button
                            intent="primary"
                            text="Create Key"
                            type="submit"
                            loading={creating}
                        />
                    </div>
                </form>
            </Dialog>

            {/* Reveal Key Dialog */}
            <Dialog
                isOpen={!!revealedKey}
                onClose={handleCloseRevealDialog}
                title="API Key Created"
                className="bp4-card-rounded"
                canOutsideClickClose={false}
            >
                <div className="p-4">
                    <Callout intent="warning" className="mb-4">
                        This key will only be shown once. Copy it now and store it securely. You
                        will not be able to see it again.
                    </Callout>
                    <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-3 py-2 rounded text-sm flex-1 break-all select-all">
                            {revealedKey}
                        </code>
                        <Button
                            icon="duplicate"
                            intent="primary"
                            text="Copy"
                            type="button"
                            onClick={handleCopyKey}
                        />
                    </div>
                </div>
                <div className="flex justify-end p-4 border-t">
                    <Button
                        text="Done"
                        intent="primary"
                        type="button"
                        onClick={handleCloseRevealDialog}
                    />
                </div>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog
                isOpen={!!revokeTarget}
                onClose={handleCloseRevokeDialog}
                title="Revoke API Key"
                className="bp4-card-rounded"
            >
                <div className="p-4">
                    <p>
                        Are you sure you want to revoke API key{' '}
                        <strong>'{revokeTarget?.name}'</strong>? This cannot be undone.
                    </p>
                    <p className="text-sm text-gray-600 mb-0">
                        Any integrations using this key will immediately stop working.
                    </p>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t">
                    <Button
                        text="Cancel"
                        type="button"
                        onClick={handleCloseRevokeDialog}
                        disabled={revokingId !== null}
                    />
                    <Button
                        intent="danger"
                        text="Revoke Key"
                        type="button"
                        loading={revokingId === revokeTarget?.id}
                        onClick={handleRevokeKey}
                    />
                </div>
            </Dialog>
        </>
    )

    return (
        <>
            <Layout
                breadcrumbs={breadcrumbs}
                primary="API Keys"
                rightActions={
                    <CreateApiKeyButton
                        onClick={handleOpenCreateDialog}
                        disabled={activeKeys.length >= 10}
                    />
                }
            >
                <div className="max-w-3xl mx-auto">{keysContent}</div>
            </Layout>
            {dialogs}
        </>
    )
}
